// src/common/hooks/useCopyToClipboard.ts
import { useCallback, useState, useRef, useEffect } from "react";

interface UseCopyToClipboardOptions {
  /** Время показа уведомления в мс (по умолчанию 1500) */
  timeout?: number;
  /** Колбэк при успешном копировании */
  onSuccess?: () => void;
  /** Колбэк при ошибке */
  onError?: (error: Error) => void;
}

export const useCopyToClipboard = (options: UseCopyToClipboardOptions = {}) => {
  const { timeout = 1500, onSuccess, onError } = options;
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Очищаем таймаут при размонтировании
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const copy = useCallback(
    async (value: string) => {
      try {
        // Основной способ через clipboard API
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(value);
        } else {
          // Fallback для старых браузеров и небезопасных контекстов
          const textarea = document.createElement("textarea");
          textarea.value = value;
          textarea.style.position = "fixed";
          textarea.style.opacity = "0";
          document.body.appendChild(textarea);
          textarea.select();
          const success = document.execCommand("copy");
          document.body.removeChild(textarea);

          if (!success) {
            throw new Error("Fallback copy failed");
          }
        }

        // Успешное копирование
        setCopied(true);
        onSuccess?.();

        // Очищаем предыдущий таймаут
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Сбрасываем состояние через timeout
        timeoutRef.current = setTimeout(() => {
          setCopied(false);
          timeoutRef.current = null;
        }, timeout);
      } catch (error) {
        console.error("Failed to copy:", error);
        onError?.(error instanceof Error ? error : new Error("Failed to copy"));
        setCopied(false);
      }
    },
    [timeout, onSuccess, onError],
  );

  // Функция для ручного сброса состояния
  const reset = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setCopied(false);
  }, []);

  return { copy, copied, reset };
};
